import { useEffect, useMemo, useState } from 'react';
import { Client, TrustSet, Wallet } from 'xrpl';
import { DEFAULT_ASSETS, loadAssetRegistry, removeAsset, saveAssetRegistry, type AssetClass, type XrplIssuedAsset } from '../assetRegistry';
import { XummLink } from './XummLink';

type Props = {
  sender: { address: string; seed?: string } | null;
  isMobile: boolean;
  txArmed?: boolean;
  xummSubmit?: (preparedTx: Record<string, unknown>, label: string) => Promise<{ txid?: string; status?: unknown }>;
};

function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
}

function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function getAccountLinesResult(respUnknown: unknown): Record<string, unknown> | null {
  if (typeof respUnknown !== 'object' || respUnknown === null) return null;
  const respObj = respUnknown as Record<string, unknown>;
  if (typeof respObj.result !== 'object' || respObj.result === null) return null;
  return respObj.result as Record<string, unknown>;
}

function hasTrustLine(linesResult: Record<string, unknown> | null, currency: string, issuer: string): boolean {
  if (!linesResult) return false;
  const lines = Array.isArray(linesResult.lines) ? (linesResult.lines as Array<unknown>) : [];
  const cur = currency.toUpperCase();
  return lines.some((lineUnknown) => {
    if (typeof lineUnknown !== 'object' || lineUnknown === null) return false;
    const line = lineUnknown as Record<string, unknown>;
    const lineCurrency = typeof line.currency === 'string' ? line.currency.toUpperCase() : '';
    const lineIssuer = typeof line.account === 'string' ? line.account : '';
    return lineCurrency === cur && lineIssuer === issuer;
  });
}

function isProbablyXrplClassicAddress(address: string): boolean {
  const a = address.trim();
  return a.startsWith('r') && a.length >= 25 && a.length <= 35;
}

function normalizeSymbol(symbol: string): string {
  return symbol.trim().toUpperCase();
}

export default function TrustlineManager({ sender, isMobile, txArmed = false, xummSubmit }: Props) {
  const [assets, setAssets] = useState<XrplIssuedAsset[]>([]);
  const [expanded, setExpanded] = useState(false);

  const [newSymbol, setNewSymbol] = useState('');
  const [newIssuer, setNewIssuer] = useState('');
  const [newName, setNewName] = useState('');
  const [newClass, setNewClass] = useState<AssetClass>('stable');
  const [newDesc, setNewDesc] = useState('');

  const [limit, setLimit] = useState('1000000');
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [linesSnapshot, setLinesSnapshot] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    (async () => {
      const loaded = await loadAssetRegistry();
      setAssets(loaded);
    })();
  }, []);

  const grouped = useMemo(() => {
    const groups: Record<AssetClass, XrplIssuedAsset[]> = { stable: [], fiat: [], rwa: [], utility: [], other: [] };
    for (const a of assets) groups[a.assetClass]?.push(a);
    return groups;
  }, [assets]);

  const canSign = !!sender?.address && (!!sender?.seed || !!xummSubmit);

  async function refreshTrustlines() {
    if (!sender?.address) {
      alert('Create a free account first so we have a sender address.');
      return;
    }
    setChecking(true);
    setLinesSnapshot(null);
    const client = new Client('wss://xrplcluster.com');
    try {
      await client.connect();
      const lines = getAccountLinesResult(
        await client.request({ command: 'account_lines', account: sender.address, ledger_index: 'validated' })
      );
      setLinesSnapshot(lines);
      alert('✓ Trust lines snapshot refreshed (validated ledger).');
    } catch (error) {
      console.error('Trust lines refresh error:', error);
      alert(`Failed to refresh trust lines: ${formatErrorMessage(error)}`);
    } finally {
      try { await client.disconnect(); } catch { /* ignore */ }
      setChecking(false);
    }
  }

  async function createTrustLine(asset: XrplIssuedAsset) {
    if (!txArmed) {
      alert('Safe Mode is ON. Arm sending on the Settlement page before submitting mainnet transactions.');
      return;
    }
    if (!sender?.address) {
      alert('No sender address available. Connect Xaman or create a free account first.');
      return;
    }
    if (!isProbablyXrplClassicAddress(asset.issuer)) {
      alert('Issuer address does not look like a classic XRPL address.');
      return;
    }
    const lim = Number(limit);
    if (!Number.isFinite(lim) || lim <= 0) {
      alert('Enter a valid trust line limit (positive number).');
      return;
    }

    // If we already have a snapshot and the line exists, warn.
    if (linesSnapshot && hasTrustLine(linesSnapshot, asset.symbol, asset.issuer)) {
      const ok = window.confirm('This trust line already appears to exist in your last snapshot. Submit anyway?');
      if (!ok) return;
    }

    const ok = window.confirm(
      `Submit TrustSet on XRPL MAINNET?\n\nAccount: ${sender.address}\nAsset: ${asset.symbol}\nIssuer: ${asset.issuer}\nLimit: ${limit}`
    );
    if (!ok) return;

    setSubmitting(true);
    const client = new Client('wss://xrplcluster.com');
    try {
      await client.connect();

      const tx: TrustSet = {
        TransactionType: 'TrustSet',
        Account: sender.address,
        LimitAmount: {
          currency: asset.symbol,
          issuer: asset.issuer,
          value: String(limit)
        }
      };
      const prepared = await client.autofill(tx);

      let txid: string | undefined;
      let submitResult: unknown = null;

      if (sender.seed) {
        const wallet = Wallet.fromSeed(sender.seed);
        const signed = wallet.sign(prepared);
        txid = signed.hash;
        submitResult = await client.submitAndWait(signed.tx_blob);
      } else if (xummSubmit) {
        const r = await xummSubmit(prepared as unknown as Record<string, unknown>, `TrustSet ${asset.symbol}`);
        txid = r?.txid;
        submitResult = r?.status;
      } else {
        alert('No signing method available (seed or Xaman).');
        return;
      }

      if (!txid) {
        alert('No transaction id returned from signing flow.');
        return;
      }

      downloadJson(`uny-trustset-${asset.symbol}-${txid}.json`, {
        type: 'XRPL_TRUSTLINE_CREATE_RECEIPT',
        createdAt: new Date().toISOString(),
        network: 'XRPL Mainnet',
        websocket: 'wss://xrplcluster.com',
        account: sender.address,
        asset,
        limit,
        txHash: txid,
        preparedTx: prepared,
        submitResult
      });

      alert(
        `✓ TRUST LINE SUBMITTED\n\nHash: ${txid}\nExplorer:\nhttps://livenet.xrpl.org/transactions/${txid}`
      );

      // Refresh snapshot after submit for better UX.
      await refreshTrustlines();
    } catch (error) {
      console.error('TrustSet submit error:', error);
      alert(`TrustSet submit failed: ${formatErrorMessage(error)}`);
    } finally {
      try { await client.disconnect(); } catch { /* ignore */ }
      setSubmitting(false);
    }
  }

  function persist(next: XrplIssuedAsset[]) {
    setAssets(next);
    saveAssetRegistry(next);
  }

  function addAsset() {
    const symbol = normalizeSymbol(newSymbol);
    if (!symbol) {
      alert('Symbol is required (e.g., USDT).');
      return;
    }
    if (symbol.length > 12) {
      alert('Keep the symbol short (<= 12 chars).');
      return;
    }
    if (!isProbablyXrplClassicAddress(newIssuer)) {
      alert('Issuer must be a valid-looking XRPL classic address.');
      return;
    }
    const name = newName.trim() || `${symbol} (IOU)`;

    const id = `${symbol}-${newIssuer}`;
    const asset: XrplIssuedAsset = {
      id,
      symbol,
      issuer: newIssuer.trim(),
      name,
      assetClass: newClass,
      description: newDesc.trim() || undefined
    };

    persist([
      ...assets.filter((a) => a.id !== id),
      asset
    ]);

    setNewSymbol('');
    setNewIssuer('');
    setNewName('');
    setNewDesc('');

    alert('✓ Asset saved to registry.');
  }

  return (
    <div style={{
      padding: isMobile ? '22px' : '28px',
      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.10) 0%, rgba(15, 23, 42, 0.25) 100%)',
      border: '1px solid rgba(34, 197, 94, 0.20)',
      borderRadius: '16px',
      marginBottom: '32px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '12px', color: '#86efac', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 800 }}>
            Asset Registry
          </div>
          <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 600, color: '#ffffff', marginTop: '8px' }}>
            Trustline Builder (clean + purpose-driven)
          </div>
          <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.6, maxWidth: '980px' }}>
            Add IOU assets once, then create trust lines with one click. This keeps your “asset classes” organized instead of scattering ad-hoc UI everywhere.
            <div style={{ marginTop: '8px', color: '#a7f3d0' }}>
              Note: XRPL IOUs are <strong>issued assets</strong>. Don’t market anything as a “stablecoin” unless you have the legal/compliance backing and redemption policy to match.
            </div>
          </div>
        </div>

        <div style={{
          padding: '12px 14px',
          background: 'rgba(0, 0, 0, 0.35)',
          border: '1px solid rgba(255, 255, 255, 0.10)',
          borderRadius: '12px',
          minWidth: '280px'
        }}>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>Sender</div>
          <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#e5e7eb', wordBreak: 'break-all' }}>
            {sender?.address || 'Not set'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 900,
            background: expanded ? '#ffffff' : 'rgba(255, 255, 255, 0.08)',
            border: expanded ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '10px',
            color: expanded ? '#000000' : '#ffffff',
            cursor: 'pointer',
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}
        >
          {expanded ? 'Hide Asset Tools' : 'Manage Assets + Trustlines'}
        </button>

        <button
          disabled={checking || !sender?.address}
          onClick={refreshTrustlines}
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 800,
            background: checking ? 'rgba(34, 197, 94, 0.25)' : 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '10px',
            color: checking ? '#9ca3af' : '#ffffff',
            cursor: (checking || !sender?.address) ? 'not-allowed' : 'pointer',
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}
        >
          {checking ? 'Refreshing…' : 'Refresh Trustlines'}
        </button>

        <button
          onClick={() => {
            persist([...DEFAULT_ASSETS]);
            alert('Reset to defaults.');
          }}
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 800,
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '10px',
            color: '#ffffff',
            cursor: 'pointer',
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}
        >
          Reset Defaults
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: '16px' }}>
          <div style={{
            padding: '14px',
            background: 'rgba(0, 0, 0, 0.35)',
            border: '1px solid rgba(34, 197, 94, 0.15)',
            borderRadius: '14px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '0.7fr 1.3fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Symbol</div>
                <input
                  value={newSymbol}
                  onChange={(e) => setNewSymbol(e.target.value)}
                  placeholder="UNY"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Issuer</div>
                <input
                  value={newIssuer}
                  onChange={(e) => setNewIssuer(e.target.value)}
                  placeholder="rIssuer..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginTop: '12px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Name</div>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Unykorn Settlement Unit"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff'
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Asset Class</div>
                <select
                  value={newClass}
                  onChange={(e) => setNewClass(e.target.value as AssetClass)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#e5e7eb'
                  }}
                >
                  <option value="stable">Stable</option>
                  <option value="fiat">Fiat</option>
                  <option value="rwa">RWA</option>
                  <option value="utility">Utility</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Description (optional)</div>
              <input
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="What is this used for?"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '10px',
                  color: '#ffffff'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
              <button
                onClick={addAsset}
                style={{
                  padding: '12px 14px',
                  fontSize: '12px',
                  fontWeight: 900,
                  background: '#86efac',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#000000',
                  cursor: 'pointer',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                Save Asset
              </button>

              <div style={{
                padding: '10px 12px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>Trust line limit</div>
                <input
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  inputMode="decimal"
                  placeholder="1000000"
                  style={{
                    width: '180px',
                    padding: '10px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '14px' }}>
            {(['stable', 'fiat', 'rwa', 'utility', 'other'] as AssetClass[]).map((cls) => {
              const items = grouped[cls] || [];
              if (!items.length) return null;
              return (
                <div key={cls} style={{
                  marginTop: '12px',
                  padding: '14px',
                  background: 'rgba(0, 0, 0, 0.28)',
                  border: '1px solid rgba(255, 255, 255, 0.10)',
                  borderRadius: '14px'
                }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {cls} assets
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                    {items.map((asset) => {
                      const exists = linesSnapshot ? hasTrustLine(linesSnapshot, asset.symbol, asset.issuer) : false;
                      return (
                        <div key={asset.id} style={{
                          padding: '12px',
                          background: 'rgba(0, 0, 0, 0.35)',
                          border: '1px solid rgba(255, 255, 255, 0.10)',
                          borderRadius: '12px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                            <div>
                              <div style={{ fontSize: '14px', color: '#ffffff', fontWeight: 700 }}>
                                {asset.symbol} — {asset.name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {asset.issuer}
                              </div>
                              {asset.description && (
                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px', lineHeight: 1.5 }}>
                                  {asset.description}
                                </div>
                              )}
                              {linesSnapshot && (
                                <div style={{ fontSize: '12px', marginTop: '8px', color: exists ? '#86efac' : '#fbbf24', fontWeight: 800 }}>
                                  Trust line: {exists ? 'PRESENT' : 'NOT FOUND'}
                                </div>
                              )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '190px' }}>
                              <button
                                disabled={!txArmed || !canSign || submitting}
                                onClick={() => createTrustLine(asset)}
                                style={{
                                  padding: '12px 14px',
                                  fontSize: '12px',
                                  fontWeight: 900,
                                  background: (!txArmed || !canSign || submitting) ? 'rgba(34, 197, 94, 0.25)' : '#ffffff',
                                  border: 'none',
                                  borderRadius: '10px',
                                  color: (!txArmed || !canSign || submitting) ? '#9ca3af' : '#000000',
                                  cursor: (!txArmed || !canSign || submitting) ? 'not-allowed' : 'pointer',
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase'
                                }}
                              >
                                {submitting ? 'Submitting…' : 'Create Trustline'}
                              </button>

                              <button
                                onClick={() => {
                                  const next = removeAsset(assets, asset.id);
                                  persist(next);
                                }}
                                style={{
                                  padding: '12px 14px',
                                  fontSize: '12px',
                                  fontWeight: 800,
                                  background: 'rgba(255, 255, 255, 0.06)',
                                  border: '1px solid rgba(255, 255, 255, 0.12)',
                                  borderRadius: '10px',
                                  color: '#ffffff',
                                  cursor: 'pointer',
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase'
                                }}
                              >
                                Remove
                              </button>

                              <XummLink
                                href={`https://livenet.xrpl.org/accounts/${asset.issuer}`}
                                style={{ color: '#a7f3d0', textDecoration: 'none', fontSize: '12px' }}
                              >
                                → View issuer
                              </XummLink>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{
            marginTop: '14px',
            padding: '12px',
            background: 'rgba(0, 0, 0, 0.35)',
            border: '1px solid rgba(255, 255, 255, 0.10)',
            borderRadius: '12px'
          }}>
            <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.6 }}>
              Tip: Keep your registry “clean” by only adding assets that have a real workflow: settlement, rewards, collateral receipts, or accounting units.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
